import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { setLanguage, getCurrentLanguage, AVAILABLE_LANGUAGES } from '../i18n';

/**
 * Language picker — a small dropdown listing every available language (LT / EN),
 * the active one highlighted. Replaces the one-button toggle whose flag+code
 * pair read as a duplicated language (finding 10, revisit 2026-09-12: user
 * wants an explicit LT/EN dropdown, not a toggle).
 */
export default function LanguageSwitcher() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const currentLang = getCurrentLanguage();
  const current = AVAILABLE_LANGUAGES.find(l => l.code === currentLang) ?? AVAILABLE_LANGUAGES[0];

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        className="flex items-center gap-0.5 px-2 py-1 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
        onClick={() => setOpen(v => !v)}
        title={t('common.language')}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('common.language')}
      >
        <span className="text-base leading-none">{current.flag}</span>
        <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50"
        >
          {AVAILABLE_LANGUAGES.map(l => (
            <button
              key={l.code}
              role="option"
              aria-selected={l.code === current.code}
              onClick={() => { setLanguage(l.code); setOpen(false); }}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors ${
                l.code === current.code
                  ? 'bg-primary-50 text-primary-800 font-medium'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="text-base">{l.flag}</span>
              <span>{l.label}</span>
              {l.code === current.code && (
                <svg className="w-4 h-4 ml-auto text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
